import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { SideMenuNode } from '@bo-client/core/navigation/main-menu.definition';
import { UserPreferenceModel } from '@bo-client/shared/models/userBackOfficeSettings';
import { UserConfigService } from '@bo-client/shared/services/user-config.service';

/**
 * Favoriten der Seitennavigation.
 *
 * Ein Favorit ist der `link` eines Blatt-Eintrags aus `MainTreeMenuData`. Die Liste liegt in den
 * Benutzereinstellungen unter `userPreferences.sidenavFavorites` (Backend, nutzerindividuell,
 * geräteübergreifend) und wird über den `UserConfigService` geschrieben.
 *
 * Angezeigt werden Favoriten nur, solange ihr Eintrag im bereits gefilterten Menübaum vorkommt
 * (`resolve`). Ein Favorit, der für Rolle, Mandant oder UI-Adaption gerade nicht zulässig ist,
 * bleibt gespeichert und erscheint wieder, sobald er zulässig ist.
 */
@Injectable({
  providedIn: 'root'
})
export class SideNavFavoritesService {
  private userConfigService = inject(UserConfigService);

  /** Gespeicherte Favoriten-Links in Anzeige-Reihenfolge. */
  favoriteLinks$: Observable<string[]> = this.userConfigService.userSettings.get$().pipe(
    map((settings) => settings?.userPreferences?.sidenavFavorites ?? []),
    distinctUntilChanged(
      (previous, current) =>
        previous.length === current.length && previous.every((link, index) => link === current[index])
    )
  );

  /** Favorisierbar sind nur Blatt-Einträge mit Link – keine Überschriften, keine Untermenüs. */
  isFavorable(node: SideMenuNode): boolean {
    return !!node?.link && !node.isTitle && !node.children?.length;
  }

  add(link: string): void {
    const links = this.storedLinks();
    if (!links.includes(link)) {
      this.save([...links, link]);
    }
  }

  remove(link: string): void {
    const links = this.storedLinks();
    if (links.includes(link)) {
      this.save(links.filter((storedLink) => storedLink !== link));
    }
  }

  /**
   * Übernimmt eine neue Reihenfolge der sichtbaren Favoriten. Gespeicherte, aber gerade nicht
   * sichtbare Favoriten behalten ihren Platz; nur die Plätze der sichtbaren werden neu belegt.
   * @param visibleLinks die Links aller sichtbaren Favoriten in der neuen Reihenfolge
   */
  reorder(visibleLinks: string[]): void {
    const visible = new Set(visibleLinks);
    const nextVisible = [...visibleLinks];
    const links = this.storedLinks().map((link) => (visible.has(link) ? nextVisible.shift() : link));
    this.save(links);
  }

  /**
   * Ordnet die Favoriten-Links den Einträgen des gefilterten Menübaums zu.
   * @param tree Menübaum nach `NavigationService.generateTreeMenu`
   * @param links gespeicherte Favoriten-Links
   * @returns je Link der erste favorisierbare Eintrag mit diesem Link (Baum-Reihenfolge);
   * Links ohne passenden Eintrag fallen heraus
   */
  resolve(tree: SideMenuNode[], links: string[]): SideMenuNode[] {
    const nodesByLink = new Map<string, SideMenuNode>();
    const collect = (nodes: SideMenuNode[]) =>
      nodes.forEach((node) => {
        if (this.isFavorable(node) && !nodesByLink.has(node.link)) {
          nodesByLink.set(node.link, node);
        }
        if (node.children?.length) {
          collect(node.children);
        }
      });
    collect(tree);
    return links.map((link) => nodesByLink.get(link)).filter((node) => !!node);
  }

  private storedLinks(): string[] {
    return this.userConfigService.userSettings.getSnapshot()?.userPreferences?.sidenavFavorites ?? [];
  }

  private save(links: string[]): void {
    const userPreferences =
      this.userConfigService.userSettings.getSnapshot()?.userPreferences ?? new UserPreferenceModel();
    this.userConfigService.userSettings.update({
      userPreferences: { ...userPreferences, sidenavFavorites: links }
    });
  }
}
